import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  NOTIFICATIONS_REFRESH_EVENT,
  TENANT_CREDENTIALS_EVENT,
  getStoredAuthRole,
} from '../../config/api-config';
import { apiService } from '../../services/api';
import type { UserNotification } from '../../services/api/types';
import { onTabVisibleRefresh } from '../../utils/tabVisibilityRefresh';
import { triggerBlobDownload } from '../../utils/documentFilenames';
import {
  NOTIFICATION_TABS,
  displayNotificationText,
  documentPdfNotificationHint,
  emptyNotificationsMessage,
  filterNotificationsByTab,
  formatNotificationWhen,
  groupNotificationsByRecency,
  isDocumentPdfDownloadable,
  notificationKindIcon,
  notificationKindLabel,
  tabCounts,
  type NotificationTabId,
} from '../../utils/notifications';
import { parseApiError } from '../common/schoolModuleUi';

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

function NotificationRow({
  item,
  downloading,
  dismissing,
  nowMs,
  onOpen,
  onDismiss,
}: {
  item: UserNotification;
  downloading: boolean;
  dismissing: boolean;
  nowMs: number;
  onOpen: (item: UserNotification) => void;
  onDismiss: (item: UserNotification) => void;
}) {
  const unread = !item.read_at;
  const when = formatNotificationWhen(item.created_at, nowMs);
  const title = displayNotificationText(item.title);
  const body = displayNotificationText(item.body);
  const kindLabel = notificationKindLabel(item.kind);
  const pdfHint = documentPdfNotificationHint(item, nowMs);
  const downloadHint = downloading ? ' · Downloading…' : '';

  return (
    <div
      className={`group relative border-b border-primary-500/10 ${
        unread ? 'bg-white/[0.03]' : ''
      }`}
    >
      <button
        type="button"
        role="menuitem"
        className="w-full text-left px-4 py-3 pr-10 hover:bg-white/5 transition-colors"
        onClick={() => onOpen(item)}
      >
        <div className="flex items-start gap-2.5">
          <span
            className={`mt-0.5 w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
              unread ? 'bg-royal-burgundy/20 text-primary-100' : 'bg-white/5 text-primary-300'
            }`}
            aria-hidden
          >
            <i className={`${notificationKindIcon(item.kind)} text-[11px]`} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              {unread && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-royal-burgundy shrink-0"
                  aria-hidden
                />
              )}
              <p className="text-[11px] uppercase tracking-wide text-primary-300/80 truncate">
                {kindLabel}
              </p>
              <p className="ml-auto text-[11px] text-primary-300/70 shrink-0" title={when.exact}>
                {when.relative}
              </p>
            </div>
            <p className="text-sm text-primary-50 font-medium mt-0.5 line-clamp-2">
              {title}
              {downloadHint}
              {pdfHint}
            </p>
            {body ? (
              <p className="text-xs text-primary-200/90 mt-0.5 line-clamp-2">{body}</p>
            ) : null}
          </div>
        </div>
      </button>
      <button
        type="button"
        className="absolute top-2.5 right-2 p-1.5 rounded text-primary-300/50 hover:text-primary-100 hover:bg-white/10 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
        aria-label="Dismiss notification"
        title="Dismiss"
        disabled={dismissing}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDismiss(item);
        }}
      >
        <i className="fas fa-times text-[10px]" aria-hidden />
      </button>
    </div>
  );
}

export const NotificationsBell: React.FC = () => {
  const history = useHistory();
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<NotificationTabId>('recent');
  const [confirmClear, setConfirmClear] = useState(false);
  const [busyAction, setBusyAction] = useState<'read-all' | 'clear-all' | string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const refreshBadge = useCallback(async () => {
    if (getStoredAuthRole() === 'super_admin') {
      setItems([]);
      setUnreadCount(0);
      setError(null);
      return;
    }
    try {
      const resp = await apiService.listNotifications({ limit: 1, unread_only: true });
      setError(null);
      setUnreadCount(resp.data?.unread_count ?? 0);
    } catch {
      /* badge refresh is best-effort */
    }
  }, []);

  const refreshList = useCallback(async () => {
    if (getStoredAuthRole() === 'super_admin') {
      setItems([]);
      setUnreadCount(0);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      const resp = await apiService.listNotifications({ limit: 80 });
      setError(null);
      setUnreadCount(resp.data?.unread_count ?? 0);
      setItems(resp.data?.notifications ?? []);
    } catch (err) {
      setItems([]);
      setError(parseApiError(err, 'Could not load notifications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshBadge();
    const onCreds = () => {
      void refreshBadge();
    };
    const onNotifyRefresh = () => {
      if (open) {
        void refreshList();
      } else {
        void refreshBadge();
      }
    };
    const stopVisibleRefresh = onTabVisibleRefresh(() => {
      if (open) {
        void refreshList();
      } else {
        void refreshBadge();
      }
    });
    window.addEventListener(TENANT_CREDENTIALS_EVENT, onCreds);
    window.addEventListener(NOTIFICATIONS_REFRESH_EVENT, onNotifyRefresh);
    return () => {
      stopVisibleRefresh();
      window.removeEventListener(TENANT_CREDENTIALS_EVENT, onCreds);
      window.removeEventListener(NOTIFICATIONS_REFRESH_EVENT, onNotifyRefresh);
    };
  }, [open, refreshBadge, refreshList]);

  useEffect(() => {
    if (!open) return;
    setConfirmClear(false);
    setNowMs(Date.now());
    void refreshList();
    const onDocClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, refreshList]);

  const counts = useMemo(() => tabCounts(items), [items]);
  const visibleItems = useMemo(() => filterNotificationsByTab(items, tab), [items, tab]);
  const groups = useMemo(
    () => groupNotificationsByRecency(visibleItems, nowMs),
    [visibleItems, nowMs]
  );

  const markReadLocal = (item: UserNotification) => {
    if (item.read_at) return;
    setUnreadCount((c) => Math.max(0, c - 1));
    setItems((prev) =>
      prev.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n))
    );
  };

  const removeLocal = (item: UserNotification) => {
    if (!item.read_at) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    setItems((prev) => prev.filter((n) => n.id !== item.id));
  };

  const onOpenItem = async (item: UserNotification) => {
    if (!item.read_at) {
      try {
        await apiService.markNotificationRead({ notification_id: item.id });
        markReadLocal(item);
      } catch {
        // still continue
      }
    }

    if (item.kind === 'document_pdf_ready' || item.kind === 'document_pdf_expired') {
      if (isDocumentPdfDownloadable(item, Date.now()) && item.entity_id) {
        setDownloadingId(item.id);
        try {
          const path = `/bulk-jobs/${encodeURIComponent(item.entity_id)}/artifact`;
          const response = await fetch(`${apiService.baseUrl}${path}`, {
            method: 'GET',
            headers: apiService.authHeaders(),
          });
          if (!response.ok) {
            throw new Error(`Download failed (${response.status})`);
          }
          const blob = await response.blob();
          const filename = filenameFromDisposition(
            response.headers.get('content-disposition'),
            'document.pdf'
          );
          triggerBlobDownload(blob, filename);
          setOpen(false);
        } catch (err) {
          window.alert(err instanceof Error ? err.message : 'Could not download PDF');
        } finally {
          setDownloadingId(null);
        }
        return;
      }
      window.alert('This PDF is no longer available. Request a new download.');
      return;
    }

    setOpen(false);
    if (item.link_path) {
      history.push(item.link_path);
    }
  };

  const onDismiss = async (item: UserNotification) => {
    setBusyAction(item.id);
    try {
      await apiService.clearNotification({ notification_id: item.id });
      removeLocal(item);
    } catch (err) {
      setError(parseApiError(err, 'Could not dismiss notification'));
    } finally {
      setBusyAction(null);
    }
  };

  const onMarkAll = async () => {
    setBusyAction('read-all');
    try {
      await apiService.markAllNotificationsRead();
      setUnreadCount(0);
      setItems((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
    } catch (err) {
      setError(parseApiError(err, 'Could not mark notifications read'));
    } finally {
      setBusyAction(null);
    }
  };

  const onClearAll = async () => {
    setBusyAction('clear-all');
    try {
      await apiService.clearAllNotifications();
      setUnreadCount(0);
      setItems([]);
      setConfirmClear(false);
    } catch (err) {
      setError(parseApiError(err, 'Could not clear notifications'));
    } finally {
      setBusyAction(null);
    }
  };

  const unreadBadge = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        className="relative p-2.5 text-primary-200 hover:text-primary-100 hover:bg-white/5 rounded-lg transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <i className="fas fa-bell" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[1.05rem] h-4 px-1 flex items-center justify-center text-[10px] font-semibold leading-none text-white bg-royal-burgundy rounded-full ring-2 ring-secondary-800">
            {unreadBadge}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-[26rem] max-h-[32rem] overflow-hidden rounded-lg border border-primary-500/25 bg-secondary-800 shadow-royal-lg z-[100] flex flex-col"
          role="menu"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-primary-500/15">
            <p className="text-sm font-medium text-primary-100">Notifications</p>
            <div className="flex items-center gap-3 shrink-0">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary-300 hover:text-primary-100 disabled:opacity-50"
                  disabled={busyAction === 'read-all'}
                  onClick={() => void onMarkAll()}
                >
                  Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary-300 hover:text-primary-100 disabled:opacity-50"
                  disabled={busyAction === 'clear-all'}
                  onClick={() => setConfirmClear(true)}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {confirmClear && (
            <div className="px-4 py-2.5 border-b border-primary-500/15 bg-white/[0.03] flex items-center justify-between gap-2">
              <p className="text-xs text-primary-200">Remove every notification?</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs text-primary-300 hover:text-primary-100"
                  onClick={() => setConfirmClear(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-xs font-medium text-royal-burgundy hover:text-primary-100 disabled:opacity-50"
                  disabled={busyAction === 'clear-all'}
                  onClick={() => void onClearAll()}
                >
                  {busyAction === 'clear-all' ? 'Clearing…' : 'Clear all'}
                </button>
              </div>
            </div>
          )}
          <div
            className="flex gap-1 px-2 pt-2 pb-1.5 overflow-x-auto border-b border-primary-500/10"
            role="tablist"
            aria-label="Notification filters"
          >
            {NOTIFICATION_TABS.map((option) => {
              const selected = tab === option.id;
              const count = counts[option.id];
              return (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap transition-colors ${
                    selected
                      ? 'bg-white/10 text-primary-50'
                      : 'text-primary-300 hover:text-primary-100 hover:bg-white/5'
                  }`}
                  onClick={() => setTab(option.id)}
                >
                  {option.label}
                  {count > 0 ? (
                    <span className="ml-1 text-primary-300/80 tabular-nums">{count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading && items.length === 0 && (
              <p className="px-4 py-6 text-sm text-primary-300/80 text-center">Loading…</p>
            )}
            {!loading && error && items.length === 0 && (
              <p className="px-4 py-6 text-sm text-red-300/90 text-center">{error}</p>
            )}
            {error && items.length > 0 && (
              <p className="px-4 py-2 text-xs text-red-300/90">{error}</p>
            )}
            {!loading && !error && visibleItems.length === 0 && (
              <p className="px-4 py-6 text-sm text-primary-300/80 text-center">
                {emptyNotificationsMessage(tab)}
              </p>
            )}
            {visibleItems.length > 0 &&
              groups.map((group) => (
                <div key={group.id}>
                  <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-wide text-primary-300/60">
                    {group.label}
                  </p>
                  {group.items.map((item) => (
                    <NotificationRow
                      key={item.id}
                      item={item}
                      downloading={downloadingId === item.id}
                      dismissing={busyAction === item.id}
                      nowMs={nowMs}
                      onOpen={(row) => void onOpenItem(row)}
                      onDismiss={(row) => void onDismiss(row)}
                    />
                  ))}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
